import { useState, useEffect, useRef } from 'react'
import { Shield, Target, Heart, Users, Award, ArrowRight, Check, Phone, MessageCircle, Instagram, Music } from 'lucide-react'

const TEAM_IMG = 'https://images.pexels.com/photos/3894376/pexels-photo-3894376.jpeg?auto=compress&cs=tinysrgb&h=650&w=940'
const MEETING_IMG = 'https://images.pexels.com/photos/7433853/pexels-photo-7433853.jpeg?auto=compress&cs=tinysrgb&h=650&w=940'
const COLLEAGUE_IMG = 'https://images.pexels.com/photos/8549944/pexels-photo-8549944.jpeg?auto=compress&cs=tinysrgb&h=650&w=940'

function useInView(threshold = 0.15) {
  const ref = useRef(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true)
    }, { threshold })
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, inView]
}

function FadeIn({ children, delay = 0, className = '' }) {
  const [ref, inView] = useInView()
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0)' : 'translateY(24px)',
      transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
    }}>{children}</div>
  )
}

const VALUES = [
  { icon: Shield, title: 'Integrity First', desc: 'Every document, every score, every audit entry — backed by evidence. No shortcuts.' },
  { icon: Heart, title: 'Built for Africa', desc: 'We understand the regulatory landscape because we live it. CAC, FIRS, sector-specific — all built in.' },
  { icon: Target, title: 'Outcomes, Not Features', desc: 'We measure success by how many companies pass due diligence, not by how many buttons we ship.' },
  { icon: Users, title: 'Community-Driven', desc: 'Our agent network, investor portal, and compliance community grow stronger together.' },
]

const MILESTONES = [
  { year: '2025', title: 'Founded', desc: 'Bizos Suite was born from the frustration of watching great African companies fail due diligence over missing documents.' },
  { year: '2025', title: 'First 100 Companies', desc: 'Reached our first 100 onboarded companies within three months of launch.' },
  { year: '2026', title: 'Agent Network', desc: 'Launched our compliance agent network, connecting companies with verified professionals across Nigeria.' },
  { year: '2026', title: 'Pan-African Vision', desc: 'Expanding to Ghana, Kenya, and South Africa with localized compliance frameworks.' },
]

export default function About({ onNavigate, onGetStarted }) {
  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-ink rounded-lg flex items-center justify-center">
              <Shield size={18} className="text-white" />
            </div>
            <span className="font-bold text-lg text-ink">Bizos Suite</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => onNavigate('landing')} className="text-sm font-medium text-neutral-700 hover:text-brand">Home</button>
            <button onClick={() => onNavigate('pricing')} className="text-sm font-medium text-neutral-700 hover:text-brand">Pricing</button>
            <button onClick={() => onNavigate('contact')} className="text-sm font-medium text-neutral-700 hover:text-brand">Contact</button>
            <button onClick={onGetStarted} className="text-sm font-semibold text-brand">Sign In</button>
            <button onClick={() => onNavigate('pricing')} className="btn-primary text-sm">Get Started</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-neutral-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-brand-soft text-brand text-xs font-semibold rounded-full mb-6">
              Our Story
            </span>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h1 className="text-4xl md:text-5xl font-bold text-ink leading-tight tracking-tight">
              We're making governance<br />accessible for African businesses
            </h1>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              Bizos Suite was founded on a simple belief: African companies deserve the same governance tools as anyone else — without the complexity, without the cost, and without needing a law degree to understand what's required.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Image */}
      <section className="px-6 pb-20">
        <FadeIn className="max-w-5xl mx-auto">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-tr from-brand-soft to-emerald-50 rounded-2xl blur-2xl opacity-50" />
            <img src={MEETING_IMG} alt="Business professionals in strategic meeting" className="relative rounded-2xl shadow-2xl w-full object-cover aspect-[16/9]" loading="lazy" />
          </div>
        </FadeIn>
      </section>

      {/* Mission */}
      <section className="py-24 px-6 bg-neutral-50">
        <div className="max-w-4xl mx-auto">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-bold text-ink tracking-tight text-center">Our Mission</h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mt-6 text-lg text-neutral-600 leading-relaxed text-center">
              To empower every African business — from early-stage startups to established enterprises — with the tools, visibility, and credibility they need to attract investment, maintain compliance, and operate with the highest standards of governance.
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-4 text-lg text-neutral-600 leading-relaxed text-center">
              We believe that governance shouldn't be a barrier to growth. It should be an advantage.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-ink tracking-tight">What we stand for</h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-8">
            {VALUES.map((value, i) => (
              <FadeIn key={value.title} delay={(i % 2) * 0.1}>
                <div className="flex gap-5 p-6 rounded-2xl border border-neutral-200 hover:shadow-lg transition-all">
                  <div className="w-14 h-14 bg-ink rounded-xl flex items-center justify-center shrink-0">
                    <value.icon size={26} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-ink mb-2">{value.title}</h3>
                    <p className="text-sm text-neutral-600 leading-relaxed">{value.desc}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 px-6 bg-neutral-50">
        <div className="max-w-4xl mx-auto">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-bold text-ink tracking-tight text-center mb-16">Our Journey</h2>
          </FadeIn>
          <div className="space-y-12">
            {MILESTONES.map((m, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="flex gap-6 items-start">
                  <div className="w-16 h-16 bg-brand-soft rounded-full flex items-center justify-center shrink-0">
                    <span className="text-xs font-bold text-brand">{m.year}</span>
                  </div>
                  <div className="flex-1 pb-12 border-l-2 border-neutral-200 pl-6 -ml-8 pt-4">
                    <h3 className="text-lg font-semibold text-ink">{m.title}</h3>
                    <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <div className="p-12 rounded-3xl bg-gradient-to-br from-ink to-neutral-800 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-brand-soft rounded-full blur-3xl opacity-20" />
              <div className="relative">
                <h2 className="text-3xl font-bold text-white">Join the movement</h2>
                <p className="mt-4 text-neutral-300">Be part of the platform transforming African business governance.</p>
                <button onClick={() => onNavigate('pricing')} className="btn-primary mt-8 flex items-center gap-2 mx-auto">
                  Get Started <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-ink rounded-lg flex items-center justify-center">
              <Shield size={18} className="text-white" />
            </div>
            <span className="font-bold text-ink">Bizos Suite</span>
          </div>
          <div className="flex flex-wrap gap-4 mb-4">
            <a href="tel:08142243764" className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-brand">
              <Phone size={14} /> 08142243764
            </a>
            <a href="https://wa.me/2348142243764" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-brand">
              <MessageCircle size={14} /> WhatsApp
            </a>
            <a href="https://instagram.com/officialbizos" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-brand">
              <Instagram size={14} /> @officialbizos
            </a>
            <a href="https://tiktok.com/@officialbizos" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-brand">
              <Music size={14} /> @officialbizos
            </a>
          </div>
          <p className="text-xs text-neutral-400">© 2026 Bizos Suite. All rights reserved. Built in Nigeria for Africa. Powered by Vobels Limited.</p>
        </div>
      </footer>
    </div>
  )
}
